<template>
  <StudioDialog
    title="Rename Video"
    showCloseButton
    :primaryActionTitle="isRenaming ? 'Saving...' : 'Save'"
    :primaryActionDisabled="!newVideoName.trim() || isRenaming"
    @primaryAction="handleRename"
    @close="handleClose"
  >
    <div class="tw-flex tw-flex-col tw-gap-4 tw-p-4">
      <label class="tw-break-words tw-text-sm">
        New name for <span class="tw-font-medium"> {{ props.videoAsset.name }}</span>
      </label>
      <div class="tw-h-[40px]">
        <StudioInput
          id="videoName"
          v-model="newVideoName"
          :placeholder="props.videoAsset.name"
          :maxlength="255"
          required
          autofocus
          @keyup.enter="handleRename"
        />
      </div>
    </div>
  </StudioDialog>
</template>

<script setup lang="ts">
import { assetsClipRenameClipPartialUpdate } from '@goldcast/api/content';
import logger from '@goldcast/logger';
import { ref } from 'vue';
import { useStore } from 'vuex';

import { renameVideoAsset, VideoAsset } from '../videoLibraryService';

import StudioDialog from '@/studioComponents/StudioDialog.vue';
import StudioInput from '@/studioComponents/StudioInput.vue';

const props = defineProps<{
  videoAsset: VideoAsset;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'success:rename', newVideoAsset: VideoAsset): void;
}>();

const store = useStore();
const newVideoName = ref('');
const isRenaming = ref(false);

const renameClip = () => {
  if (
    props.videoAsset.source === 'CL_UPLOAD' ||
    props.videoAsset.source === 'CLIP' ||
    props.videoAsset.source === 'EVENT_RECORDING' ||
    props.videoAsset.source === 'DIRECT_RECORDING'
  ) {
    return assetsClipRenameClipPartialUpdate({
      body: {
        video_library_id: props.videoAsset.id,
        title: newVideoName.value
      }
    });
  }

  return renameVideoAsset(props.videoAsset.id, newVideoName.value);
};

const handleRename = () => {
  if (!newVideoName.value.trim()) {
    return;
  }

  isRenaming.value = true;

  renameClip()
    .then(() => {
      store.dispatch('studioToast/triggerToast', {
        mode: 'success',
        msg: 'Video renamed successfully.'
      });
      emit('success:rename', { ...props.videoAsset, name: newVideoName.value });
    })
    .catch((errorMessage: string) => {
      logger.error('[VideoLibraryRenamePopup] Error renaming video', errorMessage);
      store.dispatch('studioToast/triggerToast', { mode: 'error', msg: errorMessage });
    })
    .finally(() => {
      isRenaming.value = false;
      emit('close');
    });
};

const handleClose = () => {
  emit('close');
};
</script>
